import { ValueGetterParams } from 'ag-grid-community'
import {
  MemberWorkMonthBody,
  MemberWorkloadStatusType,
  MemberWorkloadStatusRow,
} from '../MemberWorkloadStatus'
import { isEstimatedActualOfRow } from './predicate'

export const getManMonth = (
  workMonth: MemberWorkMonthBody | undefined
): number => {
  if (workMonth && workMonth.manMonth) {
    const manMonth = Number(workMonth.manMonth)
    return Number.isNaN(manMonth) ? 0 : manMonth
  }
  return 0
}

export const calculateTotalColumnValue = (
  params: ValueGetterParams
): number | undefined => {
  const row: MemberWorkloadStatusRow = params.data
  if (!params.api || !row || !params.node) return undefined

  let total = 0
  const allColumns = params.api?.getColumns() || []

  allColumns.forEach(col => {
    const colId = col.getColId()
    if (colId && /^\d{4}\/\d{2}\/\d{2}?$/.test(colId) && params.node) {
      const value = params.api.getCellValue({
        colKey: colId,
        rowNode: params.node,
      })
      if (typeof value === 'number' && !isNaN(value)) {
        total += value
      }
    }
  })

  return total > 0 ? total : undefined
}

export const calculateWorkMonthDisplayValue = (
  params: ValueGetterParams,
  yearMonth: string,
  summaryTargetRowType: MemberWorkloadStatusType,
  getRowGroupColumnKey: (row: MemberWorkloadStatusRow) => string | undefined
): number | undefined => {
  const rawValue = calculateWorkMonthColumnValue(
    params,
    yearMonth,
    summaryTargetRowType,
    getRowGroupColumnKey
  )
  if (rawValue === undefined) return undefined

  const workingDayCalendars = params.context?.workingDayCalendars
  const workloadUnit = params.context?.workloadUnit
  const dailyWorkHours = params.context?.workloadUnitState?.dailyWorkHours
  const timeGrain = params.context?.timeGrain

  let lookupKey = yearMonth

  if (timeGrain === 'WEEK' && /^\d{4}\/\d{2}\/\d{2}$/.test(yearMonth)) {
    if (workloadUnit === 'MONTH') {
      const yearMonthMatch = yearMonth.match(/^(\d{4}\/\d{2})\/\d{2}$/)
      if (yearMonthMatch) {
        lookupKey = yearMonthMatch[1]
      }
    }
  }

  const monthData = workingDayCalendars?.[lookupKey]
  if (!monthData) return undefined

  let divisor: number
  switch (workloadUnit) {
    case 'MONTH':
      divisor = monthData.workHours
      break
    case 'DAY':
      divisor = dailyWorkHours
      break
    case 'HOUR':
    default:
      divisor = 1
      break
  }

  return divisor > 0 ? rawValue / divisor : undefined
}

export const calculateWorkMonthColumnValue = (
  params: ValueGetterParams,
  yearMonth: string,
  summaryTargetRowType: MemberWorkloadStatusType,
  getRowGroupColumnKey: (row: MemberWorkloadStatusRow) => string | undefined
): number | undefined => {
  const row: MemberWorkloadStatusRow = params.data
  if (!params.api || !row) return

  if (row.type === MemberWorkloadStatusType.MEMBER) {
    const uuid = getRowGroupColumnKey(row)
    if (!uuid) return undefined
    let total = 0
    let isSum = false
    params.api.forEachNode(node => {
      const nodeData: MemberWorkloadStatusRow | undefined = node.data
      if (!nodeData) return 0
      if (
        getRowGroupColumnKey(node.parent?.data) === uuid &&
        nodeData.type === summaryTargetRowType
      ) {
        const work = nodeData.body.memberWorkMonths?.find(
          cell => cell.yearMonth === yearMonth
        )
        total += getManMonth(work)
        if (!isSum) {
          isSum = work ? true : false
        }
      }
    })
    return isSum ? total : undefined
  }
  if (row.type === MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL) {
    const uuid = getRowGroupColumnKey(row)
    if (!uuid) return undefined
    let total = 0
    let isSum = false
    params.api.forEachNode(node => {
      const nodeData: MemberWorkloadStatusRow | undefined = node.data
      if (!nodeData) return
      if (isEstimatedActualOfRow(nodeData, row, getRowGroupColumnKey)) {
        const work = nodeData.body.memberWorkMonths?.find(
          cell => cell.yearMonth === yearMonth
        )
        total += getManMonth(work)
        if (!isSum) {
          isSum = work ? true : false
        }
      }
    })
    return isSum ? total : undefined
  }

  const memberWorkMonth = !!row.body.memberWorkMonths
    ? row.body.memberWorkMonths.find(
        cell => cell.yearMonth === yearMonth && cell.manMonth !== undefined
      )
    : undefined
  return memberWorkMonth ? memberWorkMonth.manMonth : undefined
}
